import { useEffect, type ReactNode } from 'react';
import { Button, Drawer, Group, Paper, Progress, Stack, cx, type PaperProps } from '@wapharma/design-system';
import { StepSwitch } from './StepSwitch';
import styles from './DrawerWizardStepCard.module.css';

function shouldIgnoreWizardEnterTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return true;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'A' || tag === 'SELECT') return true;
  if (target.closest('button, a, [role="button"], [data-wp-enter-ignore]')) return true;
  return false;
}

export type DrawerWizardPaperProps = Omit<PaperProps, 'withBorder' | 'shadow'> & {
  children?: ReactNode;
};

/** Soft-bordered drawer/wizard card shell — hairline border + quiet popover shadow. */
export function DrawerWizardPaper({
  className,
  p = 'md',
  radius = 'comfortable',
  children,
  ...rest
}: DrawerWizardPaperProps) {
  return (
    <Paper withBorder p={p} radius={radius} className={cx(styles.card, className)} {...rest}>
      {children}
    </Paper>
  );
}

export type DrawerWizardStepCardProps = {
  title: string;
  description?: string;
  children?: ReactNode;
  className?: string;
  gap?: 'sm' | 'md' | 'lg';
  /** @deprecated Outer Paper owns padding; kept for call-site compat. */
  padding?: 'none' | 'xs' | 'sm' | 'md' | 'lg';
  /**
   * When true (default), wrap in {@link DrawerWizardPaper}.
   * Set false inside {@link DrawerWizardLayout} / {@link DrawerWizardStepSwitch}.
   */
  withPaper?: boolean;
};

/**
 * Drawer wizard step content — title + fields.
 * Standalone wizards keep Paper; Layout/StepSwitch already provide the card shell.
 */
export function DrawerWizardStepCard({
  title,
  description,
  children,
  className,
  gap = 'sm',
  padding = 'md',
  withPaper = true,
}: DrawerWizardStepCardProps) {
  const body = (
    <Stack gap={children ? gap : 'sm'} className={withPaper ? undefined : className}>
      <Stack gap={description ? 4 : 0}>
        <p className={styles.title}>{title}</p>
        {description ? <p className={styles.description}>{description}</p> : null}
      </Stack>
      {children}
    </Stack>
  );
  if (!withPaper) return body;
  return (
    <DrawerWizardPaper p={padding} className={className}>
      {body}
    </DrawerWizardPaper>
  );
}

/** Stable Paper + login-style step switch for custom Drawer bodies. */
export function DrawerWizardStepSwitch({
  stepIndex,
  children,
  className,
  paperClassName,
}: {
  stepIndex: number;
  children: ReactNode;
  className?: string;
  paperClassName?: string;
}) {
  return (
    <DrawerWizardPaper className={cx(styles.stepSwitchPaper, paperClassName)}>
      <StepSwitch index={stepIndex} mode="snapshot" className={className}>
        {children}
      </StepSwitch>
    </DrawerWizardPaper>
  );
}

export function DrawerWizardStepMeta({ children }: { children: ReactNode }) {
  return <p className={styles.stepMeta}>{children}</p>;
}

/** Segmented progress as drawer header bottom edge — label above, bar flush. */
export function DrawerWizardHeaderProgress({
  value,
  max,
  label,
  'aria-label': ariaLabel,
}: {
  value: number;
  max: number;
  label?: string;
  'aria-label'?: string;
}) {
  return (
    <div className={styles.headerProgressBlock}>
      {label ? <p className={styles.headerProgressLabel}>{label}</p> : null}
      <Progress
        value={value}
        max={max}
        size="sm"
        tone="neutral"
        className={styles.headerProgress}
        aria-label={ariaLabel ?? label}
      />
    </div>
  );
}

export type DrawerWizardNavFooterProps = {
  step: number;
  lastStep: number;
  onCancel: () => void;
  onBack: () => void;
  /**
   * Advance only — do not set form/banner errors here.
   * Field errors come from settle (blur/idle). Gate with `continueDisabled`.
   */
  onContinue: () => void;
  onSubmit?: () => void;
  submitLabel: string;
  continueLabel?: string;
  pending?: boolean;
  continuePending?: boolean;
  continueDisabled?: boolean;
  submitDisabled?: boolean;
};

export type DrawerWizardNavFooterLegacyProps = {
  backLabel: string;
  onBack: () => void;
  backDisabled?: boolean;
  children: ReactNode;
  className?: string;
};

export type DrawerWizardLayoutProps = {
  opened: boolean;
  onClose: () => void;
  title: string;
  stepIndex: number;
  stepCount: number;
  stepLabel: string;
  footer: ReactNode;
  children: ReactNode;
  /** Form-level error / status — outside the traveling step pane. */
  banner?: ReactNode;
  innerClassName?: string;
  bodyClassName?: string;
  /** Extra class on close IconButton (e.g. hide while incomplete). */
  closeClassName?: string;
};

/** Sticky drawer footer: Annuler/Précédent + Continuer/submit. */
export function DrawerWizardNavFooter(props: DrawerWizardNavFooterProps): ReactNode;
export function DrawerWizardNavFooter(props: DrawerWizardNavFooterLegacyProps): ReactNode;
export function DrawerWizardNavFooter(
  props: DrawerWizardNavFooterProps | DrawerWizardNavFooterLegacyProps,
): ReactNode {
  if ('backLabel' in props) {
    const { backLabel, onBack, backDisabled, children, className } = props;
    return (
      <div className={cx(styles.navFooterInner, className)}>
        <Group justify="space-between" align="center" wrap="wrap" gap="sm">
          <Button type="button" variant="soft" disabled={backDisabled} onClick={onBack}>
            {backLabel}
          </Button>
          <Group gap="sm" wrap="wrap">
            {children}
          </Group>
        </Group>
      </div>
    );
  }

  const {
    step,
    lastStep,
    onCancel,
    onBack,
    onContinue,
    onSubmit,
    submitLabel,
    continueLabel = 'Continuer',
    pending = false,
    continuePending = false,
    continueDisabled = false,
    submitDisabled = false,
  } = props;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      if (e.defaultPrevented || e.isComposing) return;
      if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      if (shouldIgnoreWizardEnterTarget(e.target)) return;
      if (pending || continuePending) return;

      if (step < lastStep) {
        if (continueDisabled) return;
        e.preventDefault();
        onContinue();
        return;
      }
      if (!onSubmit || submitDisabled) return;
      e.preventDefault();
      onSubmit();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    step,
    lastStep,
    onContinue,
    onSubmit,
    pending,
    continuePending,
    continueDisabled,
    submitDisabled,
  ]);

  return (
    <div className={styles.navFooterInner}>
      <Group justify="space-between" align="center" wrap="wrap" gap="sm">
        <Button
          type="button"
          variant="soft"
          onClick={step === 0 ? onCancel : onBack}
          disabled={pending || continuePending}
        >
          {step === 0 ? 'Annuler' : 'Précédent'}
        </Button>
        {step < lastStep ? (
          <Button
            type="button"
            variant="primary"
            onClick={onContinue}
            disabled={pending || continueDisabled}
            loading={continuePending}
          >
            {continueLabel}
          </Button>
        ) : (
          <Button
            type="button"
            variant="primary"
            onClick={onSubmit}
            disabled={pending || submitDisabled}
            loading={pending}
          >
            {submitLabel}
          </Button>
        )}
      </Group>
    </div>
  );
}

/** Top placement drawer with title, step progress, and centered step body. */
export function DrawerWizardLayout({
  opened,
  onClose,
  title,
  stepIndex,
  stepCount,
  stepLabel,
  footer,
  children,
  banner,
  innerClassName,
  bodyClassName,
  closeClassName,
}: DrawerWizardLayoutProps) {
  const safeCount = Math.max(1, stepCount);
  const safeIndex = Math.min(Math.max(0, stepIndex), safeCount - 1);

  return (
    <Drawer
      placement="top"
      opened={opened}
      onClose={onClose}
      title={title}
      closeClassName={closeClassName}
      bodyClassName={cx(styles.layoutBody, bodyClassName)}
      headerBottom={
        <DrawerWizardHeaderProgress
          value={safeIndex + 1}
          max={safeCount}
          label={`Étape ${safeIndex + 1} sur ${safeCount} — ${stepLabel}`}
          aria-label={`Étape ${safeIndex + 1} sur ${safeCount}`}
        />
      }
      footer={footer}
    >
      <div className={cx(styles.layoutInner, innerClassName)}>
        {banner ? <div className={styles.layoutBanner}>{banner}</div> : null}
        <DrawerWizardStepSwitch stepIndex={safeIndex}>{children}</DrawerWizardStepSwitch>
      </div>
    </Drawer>
  );
}

export function DrawerWizardShell({
  children,
  narrow,
  className,
}: {
  children: ReactNode;
  narrow?: boolean;
  className?: string;
}) {
  return <div className={cx(styles.shell, narrow && styles.shellNarrow, className)}>{children}</div>;
}

export function DrawerWizardFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <footer className={cx(styles.wizardFooter, className)}>{children}</footer>;
}

/** Centered step column — same width rhythm as product create. */
export function DrawerWizardBody({
  children,
  className,
  narrow,
}: {
  children: ReactNode;
  className?: string;
  narrow?: boolean;
}) {
  return <div className={cx(styles.bodyInner, narrow && styles.bodyInnerNarrow, className)}>{children}</div>;
}

export { styles as drawerWizardStyles };
